/* eslint-disable prettier/prettier */ 'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger, Button, ErrorTooltip } from '@whitbread-eos/atoms/ui';
import { useTranslation, formatIBAssetsUrl } from '@whitbread-eos/utils/server';
import Image from 'next/image';
import { useState, useEffect, useRef } from 'react';
import { RoomOccupancyDropdown } from './RoomOccupancyDropdown';
const getSumLabel = (value, singleLabel, multipleLabel, separator = false)=>{
    if (value) {
        return `${value} ${value > 1 ? multipleLabel : singleLabel}${separator ? ', ' : ''}`;
    }
    return '';
};
const RoomOccupancy = ({ rooms, updateRoom, formLabels, icons, addRoomIcon, addRoom, removeRoom, userRole, showError, onOpenChange, maxRooms })=>{
    const { t } = useTranslation();
    const dropdownRef = useRef(null);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const toggleDropdown = ()=>{
        setPopoverOpen(!popoverOpen);
    };
    const getLabel = ()=>{
        var _Object_values;
        const guests = (_Object_values = Object.values(rooms)) === null || _Object_values === void 0 ? void 0 : _Object_values.reduce((guests, room)=>{
            guests.adults += room === null || room === void 0 ? void 0 : room.adults;
            guests.children += room === null || room === void 0 ? void 0 : room.children;
            return guests;
        }, {
            adults: 0,
            children: 0
        });
        const adultsText = getSumLabel(guests.adults, t('content.global.adult'), t('content.global.adults'), true);
        const childrenText = getSumLabel(guests.children, t('content.global.child'), t('content.global.children'), true);
        const roomsText = getSumLabel(Object.keys(rooms).length, t('content.global.room'), t('content.global.rooms'));
        return `${adultsText}${childrenText}${roomsText}`;
    };
    useEffect(()=>{
        if (dropdownRef === null || dropdownRef === void 0 ? void 0 : dropdownRef.current) {
            var _dropdownRef_current;
            dropdownRef.current.scrollTo({
                top: dropdownRef === null || dropdownRef === void 0 ? void 0 : (_dropdownRef_current = dropdownRef.current) === null || _dropdownRef_current === void 0 ? void 0 : _dropdownRef_current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [
        Object.keys(rooms).length
    ]);
    const renderButton = /*#__PURE__*/ _jsxs(Button, {
        variant: "roomOccupancyButton",
        size: "roomOccupancyButton",
        className: `${buttonStyle}  ${showError ? errorStyle : ''} ${popoverOpen ? focusedButtonStyle : ''} `,
        "data-testid": "Room-Occupancy-Button",
        children: [
            /*#__PURE__*/ _jsx(Image, {
                className: iconStyle,
                src: formatIBAssetsUrl(formLabels === null || formLabels === void 0 ? void 0 : formLabels.guestIcon),
                width: 24,
                height: 24,
                alt: "Room Occupancy Icon"
            }),
            /*#__PURE__*/ _jsx("span", {
                className: labelStyle,
                children: getLabel()
            })
        ]
    });
    useEffect(()=>{
        onOpenChange(popoverOpen);
    }, [
        popoverOpen
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Popover, {
                open: popoverOpen,
                onOpenChange: setPopoverOpen,
                children: [
                    showError ? /*#__PURE__*/ _jsx(ErrorTooltip, {
                        icon: formatIBAssetsUrl(icons['icon.notification.error']),
                        content: t('content.form.invalidRooms'),
                        className: errorTooltipStyle,
                        open: showError,
                        testId: "IB-Room-Occupancy-ErrorTooltip",
                        children: /*#__PURE__*/ _jsx(PopoverTrigger, {
                            asChild: true,
                            children: renderButton
                        })
                    }) : /*#__PURE__*/ _jsx(PopoverTrigger, {
                        asChild: true,
                        children: renderButton
                    }),
                    /*#__PURE__*/ _jsx(PopoverContent, {
                        align: "start",
                        avoidCollisions: false,
                        "data-testid": "Room-Occupancy-Dropdown",
                        className: popoverStyle,
                        ref: dropdownRef,
                        children: /*#__PURE__*/ _jsx(RoomOccupancyDropdown, {
                            rooms: rooms,
                            formLabels: formLabels,
                            icons: icons,
                            updateRoom: updateRoom,
                            addRoom: addRoom,
                            removeRoom: removeRoom,
                            addRoomIcon: addRoomIcon,
                            userRole: userRole,
                            toggleDropdown: toggleDropdown,
                            maxRooms: maxRooms
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ErrorTooltip, {
                icon: formatIBAssetsUrl(icons['icon.notification.error']),
                content: t('content.form.invalidRooms'),
                open: showError,
                testId: "IB-Room-Occupancy-ErrorTooltip-Mobile",
                mobile: true
            })
        ]
    });
};
export default RoomOccupancy;
const buttonStyle = 'flex justify-start gap-3 border-r-0  w-full min-w-[16.5rem] tablet:min-w-48 relative mobile:border-r mobile:w-full mobile:min-w-[unset] mobile:border-l-[1px] mobile:rounded';
const focusedButtonStyle = '-outline-offset-2 outline outline-primaryColor outline-2 hover:-outline-offset-2 hover:outline hover:outline-primaryColor hover:outline-2';
const iconStyle = 'w-6 h-6';
const labelStyle = 'truncate';
const popoverStyle = 'py-6 w-80 overflow-scroll max-h-[47.5rem] mobile:w-full mobile:rounded-none mobile:h-[calc(100svh-5rem)] mobile:max-h-[unset]';
const errorStyle = '-outline-offset-2 outline outline-2 outline-error hover:-outline-offset-2 hover:outline hover:outline-error hover:outline-2';
const errorTooltipStyle = 'max-w-[90%]';
